import { useId, useMemo, useRef, useState } from 'react';
import { Languages, BedDouble, Bath, Droplets, Car, Maximize2, Layers, Users, Calendar, DoorOpen } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { Field, Input, FormActions } from '../ui/Field';
import Modal from '../ui/Modal';
import { CollapsibleSection } from '../ui/CollapsibleSection';
import CurrencyInput from '../ui/CurrencyInput';
import SpecsBar from '../ui/SpecsBar';
import { HelpTooltip } from '../ui/HelpTooltip';
import { FieldRenderer } from './FieldRenderer';
import AiAssist from './AiAssist';
import { api } from '../../lib/api';
import type { CollectionTypeDef, CollectionItem, FieldDef, FieldType, Localized } from '../../lib/types';
import { slugify, fieldExample, fieldHelp, fieldLabel, type Locale } from '../../lib/collections';

interface Props {
  type: CollectionTypeDef;
  initial?: CollectionItem;
  // Datos para precargar un alta NUEVA (p.ej. generada con IA). No cambia la
  // semántica create/update: `initial` sigue mandando.
  prefillData?: Record<string, unknown>;
  onSave: (item: CollectionItem) => void;
  onCancel: () => void;
}

function specIcon(key: string): LucideIcon | undefined {
  const k = key.toLowerCase();
  if (/recamara|bedroom|habitacion/.test(k)) return BedDouble;
  if (/(medio|half).*(bano|bath)|(bano|bath).*(medio|half)/.test(k)) return Droplets;
  if (/bano|bathroom/.test(k)) return Bath;
  if (/estacion|parking|garage|cajon/.test(k)) return Car;
  if (/superficie|area|m2|sqm|sqft/.test(k)) return Maximize2;
  if (/nivel|piso|floor|planta/.test(k)) return Layers;
  if (/capacidad|capacity/.test(k)) return Users;
  if (/antiguedad|year_built|age/.test(k)) return Calendar;
  if (/cuarto|room|ambiente/.test(k)) return DoorOpen;
  return undefined;
}

// Campos reservados (contrato §4): siempre presentes, siempre localizados text.
const TITLE: FieldDef = {
  key: 'title',
  type: 'text',
  localized: true,
  required: true,
  label: { es: 'Título', en: 'Title' },
  help: { es: 'Nombre público con el que se mostrará este elemento en el sitio.', en: 'Public name used to display this item on the site.' },
};
const SLUG: FieldDef = { key: 'slug', type: 'text', localized: true, required: true, label: { es: 'Slug', en: 'Slug' } };

// Campos cortos: se distribuyen en una retícula limitada en desktop para evitar
// inputs excesivamente anchos. Editores, media, listas y relaciones van aparte.
const COMPACT: FieldType[] = ['text', 'number', 'price', 'select'];
const isCompact = (f: FieldDef) => COMPACT.includes(f.type);

// Una sección se renderiza como una lista de "runs": tramos consecutivos de
// campos compactos (grid 2-col), tramos de booleanos (fila de toggles) y campos
// pesados a ancho completo. Preserva el orden de autoría del schema.
type Run =
  | { kind: 'compact'; fields: FieldDef[] }
  | { kind: 'boolean'; fields: FieldDef[] }
  | { kind: 'full'; field: FieldDef };

function groupFields(fields: FieldDef[]): Run[] {
  const runs: Run[] = [];
  for (const f of fields) {
    const last = runs[runs.length - 1];
    if (f.type === 'boolean') {
      if (last?.kind === 'boolean') last.fields.push(f);
      else runs.push({ kind: 'boolean', fields: [f] });
    } else if (isCompact(f)) {
      if (last?.kind === 'compact') last.fields.push(f);
      else runs.push({ kind: 'compact', fields: [f] });
    } else {
      runs.push({ kind: 'full', field: f });
    }
  }
  return runs;
}

// Chip sutil junto al label que indica que el campo es traducible y qué idioma
// se está editando ahora mismo. Reemplaza el hint textual repetido.
function LocaleChip({ locale }: { locale: Locale }) {
  return (
    <span className="inline-flex items-center gap-1 text-[10px] font-medium uppercase tracking-wide text-ui-text-subtle">
      <Languages size={11} />
      {locale}
    </span>
  );
}

// Editor generado desde el `schemaJson` del tipo. Construye el `data` nested-
// bilingüe del contrato y lo manda al CRUD genérico. Un toggle ES/EN global
// cambia qué idioma se edita en los campos localizados.
export default function CollectionForm({ type, initial, prefillData, onSave, onCancel }: Props) {
  const formId = useId();
  const [data, setData] = useState<Record<string, unknown>>(() => {
    if (initial) return { ...initial.data };
    const defaults: Record<string, unknown> = {};
    for (const f of type.schema) {
      if (f.default !== undefined) defaults[f.key] = f.default;
    }
    return { ...defaults, ...(prefillData ?? {}) };
  });
  const [status, setStatus] = useState<'draft' | 'published'>(initial?.status ?? 'published');
  const [featured, setFeatured] = useState<boolean>(initial?.featured ?? false);
  const [locale, setLocale] = useState<Locale>('es');
  const [slugEditing, setSlugEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const submittingRef = useRef(false);
  const [error, setError] = useState('');

  // Agrupa los campos del schema por `section`, preservando orden de aparición.
  const sections = useMemo(() => {
    const order: string[] = [];
    const map = new Map<string, FieldDef[]>();
    for (const f of type.schema) {
      const sec = f.section ?? 'General';
      if (!map.has(sec)) { map.set(sec, []); order.push(sec); }
      map.get(sec)!.push(f);
    }
    return order.map((sec) => ({ sec, fields: map.get(sec)! }));
  }, [type.schema]);

  function getVal(field: FieldDef): unknown {
    const raw = data[field.key];
    if (field.localized) return (raw as Localized | undefined)?.[locale];
    return raw;
  }

  function setVal(field: FieldDef, value: unknown) {
    setData((d) => {
      if (field.localized) {
        const prev = (d[field.key] as Localized | undefined) ?? {};
        return { ...d, [field.key]: { ...prev, [locale]: value } };
      }
      return { ...d, [field.key]: value };
    });
  }

  // Escribe el valor generado por IA. Para campos localized el backend devuelve
  // { es, en } y se setean AMBOS idiomas a la vez; para escalares, el valor directo.
  function applyGenerated(field: FieldDef, value: unknown) {
    setData((d) => ({ ...d, [field.key]: value }));
  }

  const canAssist = (f: FieldDef) => f.type === 'text' || f.type === 'richtext';

  // Título → autollena el slug del MISMO locale si está vacío.
  function setTitle(value: string) {
    setData((d) => {
      const prevTitle = (d.title as Localized | undefined) ?? {};
      const prevSlug = (d.slug as Localized | undefined) ?? {};
      const slugLoc = prevSlug[locale];
      const wasAuto = !slugLoc || slugLoc === slugify(prevTitle[locale] ?? '');
      return {
        ...d,
        title: { ...prevTitle, [locale]: value },
        slug: wasAuto ? { ...prevSlug, [locale]: slugify(value) } : prevSlug,
      };
    });
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (submittingRef.current) return;
    submittingRef.current = true;
    setSaving(true);
    setError('');
    try {
      // Garantiza que title/slug existan como objetos de idioma.
      const payloadData: Record<string, unknown> = {
        ...data,
        title: (data.title as Localized) ?? {},
        slug: (data.slug as Localized) ?? {},
      };
      const body = { data: payloadData, status, featured };
      const saved = initial
        ? await api.collections.update(type.key, initial.id, body)
        : await api.collections.create(type.key, body);
      onSave(saved);
    } catch (err) {
      setError(initial
        ? 'No se pudieron guardar los cambios. Revisa la información e intenta de nuevo.'
        : 'No se pudo crear el contenido. Revisa la información e intenta de nuevo.');
    } finally {
      submittingRef.current = false;
      setSaving(false);
    }
  }

  // Label con asterisco de requerido + chip de idioma si es traducible.
  const labelNode = (f: FieldDef) => (
    <span className="inline-flex items-center gap-2">
      <span>{fieldLabel(f)}{f.required ? ' *' : ''}</span>
      {f.localized && <LocaleChip locale={locale} />}
      {fieldHelp(f, locale) && <HelpTooltip text={fieldHelp(f, locale)} />}
    </span>
  );

  // Label compacto (mini) para celdas densas de specs/selects.
  const miniLabel = (f: FieldDef) => `${fieldLabel(f)}${f.required ? ' *' : ''}`;

  const exampleText = (f: FieldDef) => {
    const example = fieldExample(f, locale);
    return example ? `Ej. ${example}` : undefined;
  };

  // Un campo etiquetado a ancho completo (pesado): label + AiAssist + control.
  // Render-function (no componente anidado) para no remontar el input en cada
  // tecleo — evita perder el foco al escribir.
  function renderFullField(f: FieldDef) {
    const locationContext = f.type === 'coordinates' ? {
      location: data.location as string | undefined,
      city: data.city as string | undefined,
      neighborhood: data.neighborhood as string | undefined,
    } : undefined;

    const hasLocationField = type.schema.some(s => s.key === 'location');
    const onSyncLocation = f.type === 'coordinates' && hasLocationField
      ? (label: string) => setData(d => ({ ...d, location: label }))
      : undefined;

    return (
      <Field key={f.key} label={labelNode(f)}>
        {canAssist(f) && (
          <div className="flex justify-end mb-1">
            <AiAssist
              collectionKey={type.key}
              fieldKey={f.key}
              itemContext={data}
              onGenerated={(value) => applyGenerated(f, value)}
            />
          </div>
        )}
        <FieldRenderer field={f} value={getVal(f)} onChange={(v) => setVal(f, v)} locale={locale} placeholder={exampleText(f)} locationContext={locationContext} onSyncLocation={onSyncLocation} />
      </Field>
    );
  }

  // Reparte n campos numéricos en grupos parejos de ≤4 para que cada SpecsBar
  // respire (sobre todo en móvil): 5 → [3,2], 6 → [3,3], 8 → [4,4].
  function chunkEven(arr: FieldDef[]): FieldDef[][] {
    const groups = Math.ceil(arr.length / 4);
    const size = Math.ceil(arr.length / groups);
    const out: FieldDef[][] = [];
    for (let i = 0; i < arr.length; i += size) out.push(arr.slice(i, i + size));
    return out;
  }

  function inlineSpan(f: FieldDef, siblings: FieldDef[]): string {
    const key = f.key.toLowerCase();
    if (f.type === 'number') return 'lg:col-span-3';
    if (f.type === 'select') return 'lg:col-span-4';
    if (/(phone|telefono|whatsapp)/.test(key)) return 'lg:col-span-3';
    if (/(city|ciudad|neighborhood|colonia)/.test(key)) return 'lg:col-span-3';
    if (/(category|categoria|icon)/.test(key) && siblings.some((field) => field.type === 'price')) {
      return 'lg:col-span-8';
    }
    if (/(category|categoria|sku|icon)/.test(key)) return 'lg:col-span-6';
    if (/(email|location|ubicacion|address|direccion|role|puesto)/.test(key)) return 'lg:col-span-6';
    if (/(summary|resumen)/.test(key)) return 'lg:col-span-12';
    return 'lg:col-span-6';
  }

  // Celda de texto/select/número. El span estima la longitud habitual, pero el
  // fallback de 6 columnas mantiene legibles campos desconocidos de schemas futuros.
  function renderCompactCell(f: FieldDef, siblings: FieldDef[]) {
    return (
      <div key={f.key} className={`col-span-1 ${inlineSpan(f, siblings)}`}>
        <div className="mb-1 flex min-h-5 items-center justify-between gap-2">
          <label className="inline-flex min-w-0 items-center gap-1.5 text-xs text-ui-text-muted" title={fieldLabel(f)}>
            <span className="truncate">{miniLabel(f)}</span>
            {f.localized && <LocaleChip locale={locale} />}
            {fieldHelp(f, locale) && <HelpTooltip text={fieldHelp(f, locale)} />}
          </label>
          {canAssist(f) && (
            <AiAssist
              collectionKey={type.key}
              fieldKey={f.key}
              itemContext={data}
              onGenerated={(value) => applyGenerated(f, value)}
            />
          )}
        </div>
        <FieldRenderer field={f} value={getVal(f)} onChange={(v) => setVal(f, v)} locale={locale} placeholder={exampleText(f)} />
      </div>
    );
  }

  // Densifica un run de campos compactos (text/number/price/select), robusto para
  // cualquier colección:
  //  • price → CurrencyInput (caja «$ monto [moneda]»). La moneda solo aparece
  //    si el schema tiene un `select` justo después (propiedades/productos);
  //    si no (menú/servicios), queda «$ monto».
  //  • ≥2 números consecutivos → SpecsBar (ficha técnica, una caja con divisores).
  //  • un número suelto + selects → grid compacto de 2–3 columnas.
  // El layout vive aquí (es multi-campo); cada control sigue escribiendo en su
  // propia `key` vía get/setVal — el contrato de colecciones no cambia.
  function renderCompactRun(fields: FieldDef[], runIdx: number) {
    type Block =
      | { kind: 'money'; price: FieldDef; currency?: FieldDef }
      | { kind: 'specs'; fields: FieldDef[] }
      | { kind: 'field'; field: FieldDef };

    const blocks: Block[] = [];
    let numRun: FieldDef[] = [];   // números consecutivos pendientes
    const endNumRun = () => {
      if (numRun.length >= 2) {
        for (const g of chunkEven(numRun)) blocks.push({ kind: 'specs', fields: g });
      } else if (numRun.length === 1) {
        blocks.push({ kind: 'field', field: numRun[0] });
      }
      numRun = [];
    };

    for (let i = 0; i < fields.length; i++) {
      const f = fields[i];
      const next = fields[i + 1];
      if (f.type === 'price') {
        endNumRun();
        if (next?.type === 'select') { blocks.push({ kind: 'money', price: f, currency: next }); i++; }
        else blocks.push({ kind: 'money', price: f });
      } else if (f.type === 'number') {
        numRun.push(f);
      } else {
        endNumRun();
        blocks.push({ kind: 'field', field: f });
      }
    }
    endNumRun();

    return (
      <div key={runIdx} className="grid grid-cols-1 gap-3 lg:grid-cols-12">
        {blocks.map((b, bi) => {
          if (b.kind === 'money') {
            const cur = b.currency;
            return (
              <div key={bi} className={`col-span-1 ${blocks.length === 1 ? 'lg:col-span-6' : 'lg:col-span-4'}`}>
                <label className="mb-1 inline-flex items-center gap-1.5 text-xs text-ui-text-muted">
                  {miniLabel(b.price)}
                  {fieldHelp(b.price, locale) && <HelpTooltip text={fieldHelp(b.price, locale)} />}
                  {cur && fieldHelp(cur, locale) && <HelpTooltip text={fieldHelp(cur, locale)} />}
                </label>
                <CurrencyInput
                  value={getVal(b.price) as number | null}
                  onValueChange={(v) => setVal(b.price, v)}
                  showCurrency={!!cur}
                  currency={cur ? ((getVal(cur) as string) ?? '') : undefined}
                  options={cur?.options}
                  onCurrencyChange={cur ? (c) => setVal(cur, c) : undefined}
                />
              </div>
            );
          }
          if (b.kind === 'specs') {
            return (
              <div key={bi} className="col-span-1 lg:col-span-12">
                <SpecsBar
                  specs={b.fields.map((f) => ({
                    label: miniLabel(f),
                    icon: specIcon(f.key),
                    value: getVal(f) as number | null,
                    onChange: (v) => setVal(f, v),
                    placeholder: fieldExample(f, locale) || undefined,
                    help: fieldHelp(f, locale) || undefined,
                  }))}
                />
              </div>
            );
          }
          return renderCompactCell(b.field, fields);
        })}
      </div>
    );
  }

  function renderRun(run: Run, idx: number) {
    if (run.kind === 'boolean') {
      return (
        <div key={idx} className="flex flex-wrap gap-x-6 gap-y-2 py-1">
          {run.fields.map((f) => (
            <FieldRenderer key={f.key} field={f} value={getVal(f)} onChange={(v) => setVal(f, v)} locale={locale} />
          ))}
        </div>
      );
    }
    if (run.kind === 'compact') {
      return renderCompactRun(run.fields, idx);
    }
    return renderFullField(run.field);
  }

  const slugVal = (getVal(SLUG) as string) ?? '';
  const title = initial
    ? `Editar — ${((initial.data.title as Localized | undefined)?.es ?? (initial.data.title as string) ?? '…')}`
    : `Nuevo — ${(type.labels.singular?.es || type.key).toLowerCase()}`;

  const toolbar = (
    <div className="px-4 sm:px-6 py-3 space-y-2">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <select
            value={status}
            onChange={(e) => setStatus(e.target.value as 'draft' | 'published')}
            className="bg-ui-bg border border-ui-border rounded-md px-2.5 py-1.5 text-sm text-ui-text focus:outline-none focus:border-ui-accent"
          >
            <option value="published">Publicado</option>
            <option value="draft">Borrador</option>
          </select>
          <label className="flex items-center gap-1.5 cursor-pointer text-sm text-ui-text-muted">
            <input type="checkbox" checked={featured} onChange={(e) => setFeatured(e.target.checked)} className="w-3.5 h-3.5 accent-ui-accent" />
            Destacado
          </label>
        </div>
        <div className="flex items-center rounded-md border border-ui-border overflow-hidden text-xs shrink-0">
          {(['es', 'en'] as Locale[]).map((l) => (
            <button
              key={l}
              type="button"
              onClick={() => setLocale(l)}
              className={`px-3 py-1.5 uppercase tracking-wider transition-colors ${
                locale === l ? 'bg-ui-accent text-ui-text-inverse' : 'text-ui-text-muted hover:text-ui-text'
              }`}
            >
              {l}
            </button>
          ))}
        </div>
      </div>
      <p className="text-xs text-ui-text-subtle">
        {status === 'published'
          ? 'Se mostrará en el sitio después de publicar los cambios.'
          : 'Solo será visible en el panel hasta que cambies su estado a Publicado.'}
      </p>
    </div>
  );

  return (
    <Modal
      title={title}
      onClose={onCancel}
      wide
      toolbar={toolbar}
      footer={<FormActions formId={formId} onCancel={onCancel} saving={saving} />}
    >
    <form id={formId} onSubmit={handleSubmit}>
      <div className="pt-5 space-y-5">
        {error && (
          <div className="rounded-md border border-red-500/25 bg-red-500/10 px-3 py-2 text-sm text-ui-text-muted">
            {error}
          </div>
        )}

        {/* Título a ancho completo + slug estilo permalink */}
        <div>
          <Field label={labelNode(TITLE)}>
            <Input
              value={(getVal(TITLE) as string) ?? ''}
              onChange={(e) => setTitle(e.target.value)}
              required={locale === 'es'}
              placeholder="Escribe el título…"
            />
          </Field>
          <div className="mt-1.5 text-sm">
            {slugEditing ? (
              <div className="flex items-center gap-2">
                <span className="shrink-0 text-ui-text-subtle">URL: /</span>
                <Input
                  value={slugVal}
                  onChange={(e) => setVal(SLUG, slugify(e.target.value))}
                  autoFocus
                  className="py-1"
                />
                <button
                  type="button"
                  onClick={() => setSlugEditing(false)}
                  className="shrink-0 text-ui-accent hover:underline"
                >
                  Listo
                </button>
              </div>
            ) : (
              <p className="text-ui-text-subtle">
                URL: <span className="text-ui-text-muted">/{slugVal || '…'}</span>
                <button
                  type="button"
                  onClick={() => setSlugEditing(true)}
                  className="ml-2 text-ui-accent hover:underline"
                >
                  Editar
                </button>
              </p>
            )}
          </div>
        </div>

        {/* Secciones del schema como acordeón — solo la primera abierta */}
        {sections.length > 0 && (
          <div className="border-t border-ui-border">
            {sections.map(({ sec, fields }, i) => (
              <CollapsibleSection
                key={sec}
                title={sec}
                defaultOpen={i === 0 || sections.length <= 3}
                meta={`${fields.length} ${fields.length === 1 ? 'campo' : 'campos'}`}
              >
                {groupFields(fields).map((run, idx) => renderRun(run, idx))}
              </CollapsibleSection>
            ))}
          </div>
        )}
        {sections.length === 0 && (
          <p className="rounded-lg border border-ui-border bg-ui-bg px-4 py-3 text-sm text-ui-text-muted">
            Esta sección no necesita información adicional. Con el título y el estado de publicación es suficiente.
          </p>
        )}
      </div>

    </form>
    </Modal>
  );
}
